import { useRef, useEffect } from 'react';
import { Box, Typography, CircularProgress, Fade } from '@mui/material';
import PostCard from './PostCard';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';

const RecentPosts = ({ posts, selectedPost, selectedProfile, onPostSelect, isLoading, isMobile }) => {
  const { t: translate } = useTranslation();
  // Create a ref to track the selected PostCard
  const selectedPostRef = useRef(null);

  // Scroll the selected PostCard into view when selectedPost changes
  useEffect(() => {
    if (selectedPost && selectedPostRef.current) {
      selectedPostRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest', // Align to the nearest edge of the container
        inline: 'start',
      });
    }
  }, [selectedPost]);

  return (
    <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 2, boxShadow: 3, height: 'calc(100vh - 240px)' }}>
      <Typography variant="h6" gutterBottom color="text.primary">
        {translate('recent_posts.recent_posts', 'Recent Posts')}
      </Typography>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          <Fade in={isLoading} timeout={500}>
            <CircularProgress size={48} sx={{ color: 'primary.main' }} />
          </Fade>
        </Box>
      ) : posts.length === 0 ? (
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            {translate('recent_posts.no_posts_found', 'No posts found for this profile.')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ height: 'calc(100vh - 300px)', overflowY: 'auto', p: 1 }}>
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              selectedProfile={selectedProfile}
              isSelected={selectedPost?.id === post.id}
              onSelect={() => onPostSelect(post)}
              isMobile={isMobile}
              ref={selectedPost?.id === post.id ? selectedPostRef : null} // Attach ref to selected PostCard
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default RecentPosts;